import axios from 'axios';
import logger from '../logger';

export default abstract class MetricsRegistry {
    abstract getModuleName(): string;

    abstract getCountersToRegister(): any[];
    abstract getHistogramsToRegister(): any[];

    public static async IncrementValidCounter(name, data) {
        try {
            const client = await import('prom-client');
            const metric = client.register.getSingleMetric(name);

            if (!metric) {
                logger.warn(`Attempted to use counter that doesn't exist: ${name}`);
                return;
            }

            'inc' in metric && metric.inc(data);
        } catch (err: any) {
            logger.warn(`Failed to increment counter for ${name}`);
            logger.warn(err.stack);
        }
    }

    public static async ObserveHistogram(name, observeNumber, data) {
        try {
            const client = await import('prom-client');
            const metric = client.register.getSingleMetric(name);

            if (!metric) {
                logger.warn(`Attempted to use histogram that doesn't exist: ${name}`);
                return;
            }

            'observe' in metric && metric.observe(data, observeNumber);
        } catch (err: any) {
            logger.warn(`Failed to observe histogram for ${name}`);
            logger.warn(err.stack);
        }
    }

    public static async GetMetrics() {
        const client = await import('prom-client');
        return await client.register.metrics();
    }

    async registerCounters() {
        const counters = this.getCountersToRegister();

        const namedCounters = counters.map((counter) => {
            return {
                ...counter,
                name: `${this.getModuleName()}_${counter.name}`,
            };
        });

        for (const namedCounter of namedCounters) {
            await this.createCounter(namedCounter);
        }
    }

    async registerHistograms() {
        const histograms = this.getHistogramsToRegister();

        const namedHistograms = histograms.map((histogram) => {
            return {
                ...histogram,
                name: `${this.getModuleName()}_${histogram.name}`,
            };
        });

        for (const namedHistogram of namedHistograms) {
            await this.createHistogram(namedHistogram);
        }
    }

    protected registerClientSideEvent(eventName, data) {
        const fullName = `bff_${this.getModuleName()}_${eventName}`;

        axios
            .post('/bff-api/metrics/counter', {
                eventName: fullName,
                data,
            })
            .catch((err) => {
                // Catch and print error, but don't stop execution.
                // We don't want to cause errors because we couldn't record some metrics.
                logger.warn(err);
            });
    }

    protected async registerServerSideEvent(eventName, data) {
        if (typeof window !== 'undefined') {
            logger.info(
                'Attempted to register a serverside event on the client.  Ignoring request.'
            );
            return;
        }

        const fullName = `bff_${this.getModuleName()}_${eventName}`;

        await MetricsRegistry.IncrementValidCounter(fullName, data);
    }

    protected registerClientSideHistogramEvent(eventName, observeNumber, data) {
        const fullName = `bff_${this.getModuleName()}_${eventName}`;

        axios
            .post('/bff-api/metrics/histogram', {
                eventName: fullName,
                observeNumber,
                data,
            })
            .catch((err) => {
                // Catch and print error, but don't stop execution.
                // We don't want to cause errors because we couldn't record some metrics.
                logger.warn(err);
            });
    }

    protected async registerServerSideHistogramEvent(eventName, observeNumber, data) {
        if (typeof window !== 'undefined') {
            logger.info(
                'Attempted to register a serverside histogram event on the client.  Ignoring request.'
            );
            return;
        }

        const fullName = `bff_${this.getModuleName()}_${eventName}`;

        await MetricsRegistry.ObserveHistogram(fullName, observeNumber, data);
    }

    private async createCounter({ name, labelNames }) {
        const client = await import('prom-client');
        const metric = client.register.getSingleMetric(name);

        if (metric) {
            return metric;
        }

        const newCounter = new client.Counter({
            name: `bff_${name}`,
            help: `bff_${name}_help`,
            labelNames,
        });

        return newCounter;
    }

    private async createHistogram({ name, labelNames, buckets }) {
        const client = await import('prom-client');
        const metric = client.register.getSingleMetric(name); // wrong name?

        if (metric) {
            return metric;
        }

        const newHistogram = new client.Histogram({
            name: `bff_${name}`,
            help: `bff_${name}_help`,
            labelNames,
            buckets,
        });

        return newHistogram;
    }
}
